import { useParams } from 'react-router-dom';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { TupleArray } from '../../utils/utilityTypes';

/**
 * A hook which provides the params of the react-router-dom with the possibility of making params required
 */
export function useSpecificParams<
  TRequiredParams extends Array<string> = Array<never>,
  TOptionalParams extends Array<string> = Array<never>
>({
  requiredParamNames,
  optionalParamNames
}: UseSpecificParamsOptions<TRequiredParams, TOptionalParams>): SpecificParams<
  TRequiredParams,
  TOptionalParams
> {
  const specificParams: Record<string, string | null> = {};
  const params = useParams();

  if (requiredParamNames) {
    for (const paramName of requiredParamNames) {
      const param = params[paramName];
      assertNotNullOrUndefined(
        param,
        `parameter "${paramName}" is required but not available`
      );
      specificParams[paramName] = param;
    }
  }

  if (optionalParamNames) {
    for (const paramName of optionalParamNames) {
      specificParams[paramName] = params[paramName] ?? null;
    }
  }

  return specificParams as SpecificParams<TRequiredParams, TOptionalParams>;
}

export type UseSpecificParamsOptions<
  TRequiredParams extends Array<string>,
  TOptionalParams extends Array<string>
> = {
  requiredParamNames?: TupleArray<TRequiredParams>;
  optionalParamNames?: TupleArray<TOptionalParams>;
};

export type SpecificParams<
  TRequiredParams extends Array<string>,
  TOptionalParams extends Array<string>
> = Record<TRequiredParams[number], string> &
  Record<
    Exclude<TOptionalParams[number], TRequiredParams[number]>,
    string | null
  >;
